import {Placement} from '@popperjs/core';
import {noop} from 'lodash';
import React, {FC, PropsWithChildren, RefObject, useEffect} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskSourcesEnum} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {InteractionTypesEnum} from '../../../../libs/shared/helpers/src/interaction/interactionTypes';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {RepositionPopover} from '../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {useInteractionContext} from '../../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {useInteractionTracking} from '../../../../libs/shared/ui/src/interactionTracking/interactionTrackingContext';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {OnboardingTipTypesEnum} from '../../../models/api/preferencesModel';
import {InfoBubbleArrow} from './infoBubbleArrow';
import {InfoBubbleDiv} from './infoBubbleCommon';
import {InfoBubbleHighlight} from './infoBubbleHighlight';
import {InfoBubbleOverlay} from './infoBubbleOverlay';
import {InfoBubblesContextProps, useInfoBubblesContext} from './infoBubblesContext';

/*
 * Props.
 */

export interface InfoBubbleInstanceProps {
  /** Ref to the target element. */
  targetRef: RefObject<HTMLElement>;
}

export interface InfoBubbleProps extends InfoBubbleInstanceProps {
  /** Which info bubble to render. */
  type: OnboardingTipTypesEnum;
  /** The position of the bubble relative to its target. */
  position: Placement;
  /** Whether to allow pointer events on the target. */
  isInteractive?: boolean;
  /** Whether the bubble should appear without an overlay. */
  isStandalone?: boolean;
  /** Whether to show a highlight pulse effect. */
  hasHighlightPulse?: boolean;
  /** Optional handler for clicks outside of the highlighted target. If provided, will prevent default click action. */
  onOutsideHighlightClick?: Handler;
  interactionId?: string;
  /** If true, doesn't add any bubble styling, just highlights the anchor */
  noBubble?: boolean;
}

/*
 * Style.
 */

const StyledInfoBubbleDiv = styled(InfoBubbleDiv)`
  position: relative;
  overflow: hidden;

  width: 410px;
  margin: 28px;

  border-radius: 16px;
  background-color: ${(p) => p.theme.foreground};
  pointer-events: all;
  ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
`;

/*
 * Component.
 */

export const InfoBubble: FC<PropsWithChildren<InfoBubbleProps & InfoBubblesContextProps>> = (props) => {
  const {
    targetRef,
    infoBubbles,
    type,
    position,
    isInteractive,
    isStandalone,
    hasHighlightPulse,
    onOutsideHighlightClick,
    children,
    interactionId,
    noBubble,
  } = props;

  const interactionContext = useInteractionContext();

  const context = useInfoBubblesContext();
  const {trackInteraction} = useInteractionTracking();

  useEffect(() => {
    if (!children) {
      return noop;
    }

    if (!infoBubbles[type]) {
      return noop;
    }

    // Scroll the target into view to avoid being stuck.
    globalWindow.requestInstrumentedIdleCallback(
      () => {
        if (targetRef.current) {
          targetRef.current.scrollIntoView();
        }
      },
      {source: TaskSourcesEnum.COMPONENT_RENDER},
    );

    trackInteraction(
      InteractionTypesEnum.INFOBUBBLE_SHOWN,
      interactionId || 'infoBubble',
      interactionContext.parentInteractionIds,
    );

    return noop;
  });

  // Only render the info bubble if we've been given some bubble content to render via children.
  // This allows us to render only the overlay on the target when necessary.
  const maybeRenderBubble = () => {
    if (!children) {
      return null;
    }

    return (
      <>
        <RepositionPopover anchor={targetRef} placement={position}>
          <StyledInfoBubbleDiv>{children}</StyledInfoBubbleDiv>
        </RepositionPopover>
        <InfoBubbleArrow anchor={targetRef} isInteractive={isInteractive} position={position} />
      </>
    );
  };

  // If the bubble should appear standalone, do not render the overlay.
  const maybeRenderOverlay = () => {
    if (isStandalone) {
      return null;
    }

    return (
      <InfoBubbleOverlay
        anchor={targetRef}
        hasPopover={Boolean(children)}
        isInteractive={isInteractive}
        closeInfoBubble={() => context.closeInfoBubbles(type)}
        noBubble={Boolean(noBubble)}
      />
    );
  };

  // Render the target with an optional highlight pulse effect.
  const maybeRenderHighlight = () => {
    if (!hasHighlightPulse) {
      return null;
    }

    return (
      <InfoBubbleHighlight
        anchor={targetRef}
        infoBubbleType={type}
        onOutsideHighlightClick={onOutsideHighlightClick}
      />
    );
  };

  if (!infoBubbles[type]) {
    return null;
  }

  return (
    <>
      {maybeRenderOverlay()}
      {maybeRenderBubble()}
      {maybeRenderHighlight()}
    </>
  );
};
